import React, { createContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { userService } from '../services/userService';

const AdminContext = createContext({
  isAdmin: false,
  accountKind: null,
  isStudent: false,
  loading: true,
});

/**
 * `accountKind` rides along here, despite the context's name, because this
 * provider ALREADY reads `users/{uid}` for `isAdmin` — a context of its own
 * would mean a second read of the same document on every page load. It is who
 * the account is: 'student' (redeemed a student preview code), 'homeowner'
 * (came in from a shared design link), or absent for a contractor.
 *
 * Set only by the server — `redeemPreviewCode` stamps it, and the rules forbid
 * a client from writing it after the doc is created — so it is safe to gate
 * affordances on.
 */
export const AdminProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [accountKind, setAccountKind] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
          const userData = await userService.getUser(currentUser.uid);
          setIsAdmin(userData?.isAdmin || false);
          setAccountKind(userData?.accountKind || null);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      } finally {
        setLoading(false);
      }
    };

    // Check admin status on mount
    checkAdminStatus();

    // Listen for auth state changes
    const unsubscribe = authService.onAuthStateChanged((user) => {
      if (user) {
        checkAdminStatus();
      } else {
        setIsAdmin(false);
        setAccountKind(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const value = {
    isAdmin,
    accountKind,
    isStudent: accountKind === 'student',
    loading,
  };

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
};

export default AdminContext;
